import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { saveBootstrapToken, signIn } from "../lib/auth";
import { isCognitoConfigured, redirectToHostedUi } from "../lib/cognito";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import CustomButton from "../components/CustomButton";

export function Login() {
  const nav = useNavigate();
  const [projectId, setProjectId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Bootstrap admin access
  const [bootstrapToken, setBootstrapToken] = useState("");
  const [bootstrapBusy, setBootstrapBusy] = useState(false);

  // Which panel is active
  const [activePanel, setActivePanel] = useState<"signin" | "admin">("signin");

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!projectId.trim()) {
      setError("Project ID is required.");
      return;
    }
    if (!apiKey.trim()) {
      setError("API key is required.");
      return;
    }
    if (/^https?:\/\//.test(apiKey.trim())) {
      setError("API key must be a token, not a URL. Enter the key provided when you created the project.");
      return;
    }
    setError(null);
    signIn(projectId.trim(), apiKey.trim());
    nav("/projects", { replace: true });
  }

  async function onBootstrap(e: FormEvent) {
    e.preventDefault();
    if (!bootstrapToken.trim()) {
      setError("Bootstrap token is required.");
      return;
    }
    setBootstrapBusy(true);
    setError(null);
    try {
      saveBootstrapToken(bootstrapToken.trim());
      nav("/organizations", { replace: true });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBootstrapBusy(false);
    }
  }

  return (
    <Box className="login-page">
      {/* Unified auth card with internal tabs */}
      <Card className="login-card login-card-wide">
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          {/* Tab bar */}
          <Box className="login-tabs">
            <button
              className={`login-tab ${activePanel === "signin" ? "active" : ""}`}
              onClick={() => { setActivePanel("signin"); setError(null); }}
            >
              Project sign in
            </button>
            <button
              className={`login-tab ${activePanel === "admin" ? "active" : ""}`}
              onClick={() => { setActivePanel("admin"); setError(null); }}
            >
              Admin access
            </button>
          </Box>

          <Box className="login-card-content">
            {activePanel === "signin" && (
              <form onSubmit={onSubmit}>
                <Typography variant="body2" className="login-subtitle">
                  Enter your project ID and API key to access Code Insights.
                </Typography>

                <Box className="login-fields">
                  <TextField
                    label="Project ID"
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    placeholder="UUID"
                    size="small"
                    fullWidth
                    autoFocus
                    className="login-input"
                  />
                  <TextField
                    label="API key"
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="ci_..."
                    size="small"
                    fullWidth
                    className="login-input"
                  />
                  <CustomButton
                    loading={false}
                    buttonText="Continue"
                    buttonTextLoading="Continuing…"
                    handleSubmitClick={() => {}}
                  />
                </Box>
              </form>
            )}

            {activePanel === "signin" && isCognitoConfigured() && (
              <>
                <Divider sx={{ my: 2 }}>or</Divider>
                <Box className="login-fields">
                  <CustomButton
                    loading={false}
                    buttonText="Log in with AWS Cognito"
                    buttonTextLoading=""
                    handleSubmitClick={() => redirectToHostedUi()}
                  />
                </Box>
              </>
            )}

            {activePanel === "admin" && (
              <form onSubmit={onBootstrap}>
                <Typography variant="body2" className="login-subtitle">
                  Enter your bootstrap token to manage organizations and projects.{" "}
                  <Link to="/organizations" className="login-link">
                    Already have one saved →
                  </Link>
                </Typography>

                <Box className="login-fields">
                  <TextField
                    label="Bootstrap token"
                    type="password"
                    value={bootstrapToken}
                    onChange={(e) => setBootstrapToken(e.target.value)}
                    placeholder="admin token from .env"
                    size="small"
                    fullWidth
                    className="login-input"
                  />
                  <CustomButton
                    loading={bootstrapBusy}
                    buttonText="Manage organizations →"
                    buttonTextLoading="Saving…"
                    handleSubmitClick={() => {}}
                  />
                </Box>
              </form>
            )}

            {/* Error alert */}
            {error && (
              <Alert severity="error" className="login-alert" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
